import type { SupabaseClient } from "@supabase/supabase-js";

// Timeclock log read model — public.management_timeclock_log (Codex contract ab6053b).
// One row per time punch; guard/device labels arrive sha-redacted server-side, and the view
// deliberately omits raw ids, notes bodies, and coordinates. Read-only by design: punches are
// CREATED only via record_time_punch(...) from the clients — this surface never mutates.

export const TIMECLOCK_PUNCH_TYPES = ["clock_in", "clock_out", "break_start", "break_end"] as const;
export type TimeclockPunchType = (typeof TIMECLOCK_PUNCH_TYPES)[number];

export interface TimeclockRow {
  timePunchId: string;
  guardLabel: string;
  routeName: string | null;
  siteName: string | null;
  routeAssignmentId?: string | null;
  siteId?: string | null;
  guardRef?: string | null;
  punchType: string;
  deviceTime: string | null;
  gpsPermission: string | null;
  locationStatus: string | null;
  gpsAccuracyLabel: string | null;
  offlineCreated: boolean;
  appVersion: string | null;
  deviceLabel: string | null;
  routeAttached: boolean;
  notesPresent: boolean;
  createdAt: string | null;
  serverReceivedAt?: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

export function mapTimeclockRow(raw: Record<string, unknown>): TimeclockRow {
  return {
    timePunchId: asString(raw.time_punch_id) ?? "",
    guardLabel: asString(raw.guard_label) ?? "Guard —",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    routeAssignmentId: asString(raw.route_assignment_id),
    siteId: asString(raw.site_id),
    guardRef: asString(raw.guard_ref),
    punchType: asString(raw.punch_type) ?? "unknown",
    deviceTime: asString(raw.device_time),
    gpsPermission: asString(raw.gps_permission),
    locationStatus: asString(raw.location_status),
    gpsAccuracyLabel: asString(raw.gps_accuracy_label),
    offlineCreated: asBoolean(raw.offline_created),
    appVersion: asString(raw.app_version),
    deviceLabel: asString(raw.device_label),
    routeAttached: asBoolean(raw.route_attached),
    notesPresent: asBoolean(raw.notes_present),
    createdAt: asString(raw.created_at),
    serverReceivedAt: asString(raw.server_received_at),
  };
}

export const TIMECLOCK_ROW_LIMIT = 200;

/** Most recent punches first; RLS scopes rows to the caller's account server-side. */
export async function fetchTimeclockLog(
  client: SupabaseClient,
  accountId: string,
  limit: number = TIMECLOCK_ROW_LIMIT,
): Promise<TimeclockRow[]> {
  const { data, error } = await client
    .from("management_timeclock_log")
    .select("*")
    .eq("account_id", accountId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error("Could not load the timeclock log.");
  }
  const rows = Array.isArray(data) ? data : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapTimeclockRow)
    .filter((row) => row.timePunchId !== "");
}
